import { useProducts, getFirstImage, getEffectivePrice } from "@/hooks/useProducts";
import { useCart } from "@/contexts/CartContext";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";

interface CartUpsellProps {
  onClose?: () => void;
  compact?: boolean;
}

const CartUpsell = ({ onClose, compact = false }: CartUpsellProps) => {
  const { items, addItem } = useCart();
  const cartProductIds = items.map((i) => i.product.id);

  const { data: products = [] } = useProducts({ limit: 12 });

  // Filter out items already in cart and show up to 4
  const suggestions = products
    .filter((p) => !cartProductIds.includes(p.id) && p.in_stock)
    .slice(0, compact ? 3 : 4);

  if (suggestions.length === 0) return null;

  return (
    <div className={compact ? "pt-3" : "pt-4"}>
      <h3 className={`font-display font-semibold mb-3 ${compact ? "text-xs uppercase tracking-wider text-muted-foreground" : "text-sm"}`}>
        You might also like
      </h3>
      <div className={`grid ${compact ? "grid-cols-3 gap-2" : "grid-cols-2 sm:grid-cols-4 gap-3"}`}>
        {suggestions.map((product) => {
          const price = getEffectivePrice(product);
          return (
            <div key={product.id} className="group relative">
              <Link
                to={`/product/${product.slug}`}
                onClick={onClose}
                className="block"
              >
                <div className="aspect-square rounded-sm overflow-hidden bg-secondary/30 mb-1.5">
                  <img
                    src={getFirstImage(product.images)}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <p className={`font-body font-medium truncate ${compact ? "text-[10px]" : "text-xs"}`}>
                  {product.name}
                </p>
                <p className={`font-display font-bold text-primary ${compact ? "text-[10px]" : "text-xs"}`}>
                  £{price.toFixed(2)}
                </p>
              </Link>
              <button
                onClick={() => {
                  addItem(product, 1);
                  toast.success(`${product.name} added to cart`);
                }}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-background/90 backdrop-blur-sm border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-primary hover:text-primary-foreground hover:border-primary"
                aria-label={`Add ${product.name} to cart`}
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CartUpsell;
